import { type Currency, formatAmount } from "@loot-os/api-contract";
import type {
  StaffLiveBreakTotalsWire,
  StaffLiveBreakWire,
} from "@loot-os/functions/src/dashboard-api/trpc/routers/liveBreakWire.js";
import { Card } from "@loot-os/ui/components/card";
import { formatNumber } from "@loot-os/ui/lib/format";

import { liveBreakDurationText } from "../lib/liveBreakStaff";
import { Stat } from "./Facts";

/** The fields of a live break the figures strip reads. */
export type LiveBreakFiguresSource = Pick<
  StaffLiveBreakWire,
  "isHouse" | "queuedCount" | "queueCap" | "viewerCount" | "createdAt" | "closedAt"
>;

/** How a figure splits beneath its total: the viewers' share and the host's own. */
interface FigureSplit {
  viewers: string;
  host: string;
}

/**
 * The strip of figures on a live break's page: queue, viewers, opens, spend and commission, each
 * split viewers vs the host beneath on a hosted live break, and its length. `now` is the moment
 * the figures were read (the detail query's `dataUpdatedAt`), so Length moves with each refetch
 * while the live break is open; a closed one measures to its `closedAt`.
 */
export function LiveBreakFigures({
  liveBreak,
  stats,
  currency,
  now,
}: {
  liveBreak: LiveBreakFiguresSource;
  stats: StaffLiveBreakTotalsWire;
  currency: Currency;
  now: Date;
}) {
  // A hosted live break splits its figures between viewers and the host's own purchases.
  const split = (viewers: string, host: string): FigureSplit | undefined =>
    liveBreak.isHouse ? undefined : { viewers, host };
  return (
    // Hairlines between the cells come from the border-coloured gap showing through. Six across
    // only from xl: a split caption needs the room, and a clipped split is no split at all.
    <Card className="grid grid-cols-2 gap-px bg-border py-0 sm:grid-cols-3 xl:grid-cols-6">
      <FigureCell label="Queue">
        {`${formatNumber(liveBreak.queuedCount)} / ${formatNumber(liveBreak.queueCap)}`}
      </FigureCell>
      <FigureCell label="Viewers">{formatNumber(liveBreak.viewerCount)}</FigureCell>
      <FigureCell
        label="Opens"
        split={split(formatNumber(stats.viewerOpens), formatNumber(stats.selfOpens))}
      >
        {formatNumber(stats.viewerOpens + stats.selfOpens)}
      </FigureCell>
      <FigureCell
        label="Spend"
        split={split(
          formatAmount(stats.viewerSpendCents, currency),
          formatAmount(stats.selfSpendCents, currency),
        )}
      >
        {formatAmount(stats.viewerSpendCents + stats.selfSpendCents, currency)}
      </FigureCell>
      <FigureCell
        label="Commission"
        split={split(
          formatAmount(stats.viewerCommissionCents, currency),
          formatAmount(stats.selfCommissionCents, currency),
        )}
      >
        {liveBreak.isHouse
          ? "—"
          : formatAmount(stats.viewerCommissionCents + stats.selfCommissionCents, currency)}
      </FigureCell>
      <FigureCell label="Length">{liveBreakDurationText(liveBreak, now)}</FigureCell>
    </Card>
  );
}

/**
 * One cell of the figures strip: the figure, and how it splits beneath when it does, each share
 * on a line of its own and wrapping rather than clipping, so the split is always readable.
 */
function FigureCell({
  label,
  split,
  children,
}: {
  label: string;
  split?: FigureSplit;
  children: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 bg-card px-5 py-4">
      <Stat label={label}>{children}</Stat>
      {split && (
        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-2 text-xs leading-tight text-muted-foreground">
          <dt>Viewers</dt>
          <dd className="min-w-0 break-words tabular-nums">{split.viewers}</dd>
          <dt>Host</dt>
          <dd className="min-w-0 break-words tabular-nums">{split.host}</dd>
        </dl>
      )}
    </div>
  );
}
